import React from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import isNewConsumerApp from 'commons/isNewConsumerApp';
import notFoundNewConsumerApp from 'commons/notFoundNewConsumerApp';
import PDPDesktop from './PDPDesktop';
import PDPMobile from './PDPMobile';
import Component404 from '../404';
import MobileAndTabletOnly from '../MobileAndTabletOnly';
import DesktopOnly from '../DesktopOnly';
import PDPSEOTags from './PDPSEOTags';
import PDPFooter from './PDPFooter';
import PDPContext from './state/context/PDPContext';
import usePDPLuxAnalytics from './hooks/usePDPLuxAnalytics';
import usePDPCreateContextValue from './hooks/usePDPCreateContextValue';
import PDPPageAnalytics from './PDPPageAnalytics';
import SuspenseWithLoader from '../SuspenseWithLoader';
import PDPPlaceholder from './PDPPlaceholder';

const PDPInner = ({
  slugOverride = null,
  isEditOrder = false,
  pdpIsModularBYOProduct = false,
}) => {
  const params = useParams();
  const slug = slugOverride ?? params.slug ?? null;

  const pdpContextValue = usePDPCreateContextValue({
    isEditOrder,
    pdpIsModularBYOProduct,
    slug,
  });

  usePDPLuxAnalytics();

  if (pdpContextValue.basicDetails.isProductNotFound) {
    if (!isNewConsumerApp) {
      return <Component404 />;
    }

    return notFoundNewConsumerApp();
  }

  const {
    discontinuedCategoryMsg = null,
    isDiscontinued = false,
    isEFGInternal,
    isOutOfStock = false,
    showNotification,
  } = pdpContextValue.stockAvailability?.data || {};

  return (
    <PDPContext.Provider value={pdpContextValue}>
      <PDPSEOTags isOutOfStock={isOutOfStock} />

      {!isNewConsumerApp && (
        // CON-6491: Temporarily, we have disabled the mobile version of PDP
        // in the new Consumer app while fixing other loading issues.
        <MobileAndTabletOnly>
          <PDPMobile
            slug={slug}
            isEditOrder={isEditOrder}
            isOutOfStock={isOutOfStock}
            showOutOfStockNotification={showNotification}
            pdpIsModularBYOProduct={pdpIsModularBYOProduct}
            showDiscontinuedNotification={isDiscontinued}
            discontinuedCategoryMsg={
              isDiscontinued ? discontinuedCategoryMsg : null
            }
          />
        </MobileAndTabletOnly>
      )}

      <DesktopOnly>
        <PDPDesktop
          slug={slug}
          isEditOrder={isEditOrder}
          isOutOfStock={isOutOfStock}
          showOutOfStockNotification={showNotification}
          pdpIsModularBYOProduct={pdpIsModularBYOProduct}
          isEFGInternal={isEFGInternal}
          showDiscontinuedNotification={isDiscontinued}
          discontinuedCategoryMsg={
            isDiscontinued ? discontinuedCategoryMsg : null
          }
        />
      </DesktopOnly>

      <PDPPageAnalytics />
      <PDPFooter />
    </PDPContext.Provider>
  );
};

const PDP = props => (
  <SuspenseWithLoader fallback={<PDPPlaceholder />}>
    <PDPInner {...props} />
  </SuspenseWithLoader>
);

PDP.propTypes = {
  slugOverride: PropTypes.string,
  isEditOrder: PropTypes.bool,
  pdpIsModularBYOProduct: PropTypes.bool,
};

export default PDP;
